import { useEffect, useState } from 'react';
import { CheckCircle2, ChevronDown, Pencil } from 'lucide-react';
import type { Blueprint } from '@/api/deep-signal-api';
import { saveCuratedBlueprint } from '@/api/deep-signal-api';
import type { InvStatus } from '../types';
import { useDeepSignal } from '../context';
import { WAVE_ACCENT, STATUS_LABEL, STATUS_STYLE, STATUS_CYCLE } from '../constants';
import { resolveCustomerContent } from '../deep-signal-content';
import { loadStatus, saveStatus } from '../builders';
import { SectionLabel, SectionTitle, DSLoadingRow, DSEmptyState } from '../components/section-primitives';
import { listContent } from '@/api/content';
import type { DraftSummary } from '@/api/content';
import { getActionList } from '@/api/action-list';
import { DraftPanel, WriteFromAction, draftLine } from '../../content/content-draft-panel';

/** An item's drafts: those written for it, and those written for the
 *  actions on the beliefs it targets (#132). */
function itemDrafts(inv: { id: string; target_variables?: string[] }, drafts: DraftSummary[]): DraftSummary[] {
  const beliefs = new Set(inv.target_variables || []);
  return drafts.filter(d => d.blueprint_item_id === inv.id || (d.belief_id != null && beliefs.has(d.belief_id)));
}

function ItemContent({ customerId, inv, drafts, actionId, isAdmin }: {
  customerId: number;
  inv: { id: string; target_variables?: string[] };
  drafts: DraftSummary[];
  actionId: number | null;
  isAdmin: boolean;
}) {
  const [open, setOpen] = useState<number | null>(null);
  const mine = itemDrafts(inv, drafts);
  if (!mine.length && !(isAdmin && actionId != null)) return null;
  return (
    <div style={{ marginTop: '12px' }} onClick={e => e.stopPropagation()}>
      {mine.length > 0 && (
        <>
          <p style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--muted-foreground)' }}>Drafts</p>
          <ul style={{ margin: '4px 0 0', paddingLeft: '18px', fontSize: 'var(--text-xs)' }}>
            {mine.map(d => (
              <li key={d.id}>
                <button onClick={() => setOpen(open === d.id ? null : d.id)} style={{
                  border: 'none', background: 'none', padding: 0, cursor: 'pointer', fontSize: 'inherit',
                  color: 'var(--alpha-teal)',
                }}>{draftLine(d)}</button>
              </li>
            ))}
          </ul>
          {open != null && <DraftPanel customerId={customerId} draftId={open} isAdmin={isAdmin} onClose={() => setOpen(null)} />}
        </>
      )}
      {isAdmin && actionId != null && (
        <div style={{ marginTop: '10px' }}>
          <WriteFromAction customerId={customerId} actionId={actionId} isAdmin={isAdmin} blueprintItemId={inv.id} />
        </div>
      )}
    </div>
  );
}

export function BlueprintSection() {
  const { apiData, loading } = useDeepSignal();
  const isAdmin = Boolean(apiData?.viewer_is_admin);

  // Admin curation: entering edit mode deep-copies the rendered blueprint
  // into a draft; Save PUTs the draft and the backend's sanitized copy
  // becomes the rendered content (savedOverride) without a refetch.
  const [savedOverride, setSavedOverride] = useState<Blueprint | null>(null);
  const [draft, setDraft] = useState<Blueprint | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const editMode = draft !== null;

  // Per-customer blueprint via the shared resolver (backend payload when
  // present, else per-customer config). No client's blueprint leaks to another.
  const resolvedBlueprint = savedOverride ?? resolveCustomerContent(apiData).blueprint;
  const blueprint = editMode ? draft : resolvedBlueprint;
  const waves = blueprint?.waves ?? [];

  // Task status is keyed by the REAL customer id so completing an intervention
  // for one brand never marks it complete for another brand on this machine.
  const customerId = apiData?.customer?.id ?? null;

  // Status map: invId -> InvStatus (initialised from localStorage on first render)
  const [statuses, setStatuses] = useState<Record<string, InvStatus>>(() => {
    const map: Record<string, InvStatus> = {};
    if (customerId != null) {
      for (const wave of waves) {
        for (const inv of wave.interventions) {
          map[inv.id] = loadStatus(customerId, inv.id);
        }
      }
    }
    return map;
  });

  // Expanded intervention ids
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // #132: each item links to its drafts; an admin can write one from the
  // open action on the belief the item targets.
  const [drafts, setDrafts] = useState<DraftSummary[]>([]);
  const [actionByBelief, setActionByBelief] = useState<Record<string, number>>({});
  useEffect(() => {
    if (customerId == null) return;
    listContent(customerId).then(r => setDrafts(r.drafts || [])).catch(() => setDrafts([]));
    if (isAdmin) {
      getActionList(customerId).then(view => {
        const map: Record<string, number> = {};
        for (const a of view.open || []) {
          const id = a.belief?.id;
          if (id && map[id] == null && !a.hidden) map[id] = a.id;
        }
        setActionByBelief(map);
      }).catch(() => setActionByBelief({}));
    }
  }, [customerId, isAdmin]);

  if (loading) return (
    <div style={{ padding: '32px 64px' }}>
      {[1,2,3].map(i => <DSLoadingRow key={i} />)}
    </div>
  );

  if (!blueprint) return (
    <DSEmptyState message="Blueprint not configured for this client yet." />
  );

  const totalInterventions = waves.reduce((n, w) => n + w.interventions.length, 0);
  const totalComplete      = Object.values(statuses).filter(s => s === 'complete').length;

  function cycleStatus(invId: string) {
    setStatuses(prev => {
      const current = prev[invId] ?? 'not_started';
      const next = STATUS_CYCLE[(STATUS_CYCLE.indexOf(current) + 1) % STATUS_CYCLE.length];
      if (customerId != null) saveStatus(customerId, invId, next);
      return { ...prev, [invId]: next };
    });
  }

  function toggleExpanded(invId: string) {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(invId) ? next.delete(invId) : next.add(invId);
      return next;
    });
  }

  function startEdit() {
    if (!resolvedBlueprint) return;
    setSaveError(null);
    setDraft(JSON.parse(JSON.stringify(resolvedBlueprint)) as Blueprint);
    // Open every intervention so all descriptions are editable at once.
    setExpanded(new Set(resolvedBlueprint.waves.flatMap(w => w.interventions.map(i => i.id))));
  }

  function updateDraftInv(waveIdx: number, invIdx: number, field: 'title' | 'description' | 'effort', value: string) {
    setDraft(prev => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev)) as Blueprint;
      const inv = next.waves[waveIdx]?.interventions[invIdx];
      if (!inv) return next;
      if (field === 'effort') {
        inv.effort = value === 'LOW' || value === 'HIGH' ? value : 'MEDIUM';
      } else {
        inv[field] = value;
      }
      return next;
    });
  }

  async function saveEdit() {
    if (!draft || customerId == null) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await saveCuratedBlueprint(customerId, draft);
      setSavedOverride(res.blueprint);
      setDraft(null);
    } catch {
      setSaveError('Save failed. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ padding: '28px 0 40px' }}>

      {/* Header */}
      <div style={{ padding: '0 32px', marginBottom: '24px' }}>
        <SectionLabel>Blueprint</SectionLabel>
        <SectionTitle size="xl">
          {`${totalInterventions} interventions across ${waves.length} waves, sequenced by expected signal velocity.`}
        </SectionTitle>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '10px' }}>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>
            {blueprint.produced_at ? `Prepared ${blueprint.produced_at}` : 'Custom action plan'}
          </p>
          {/* Admin-only curation controls */}
          {isAdmin && !editMode && (
            <button
              onClick={startEdit}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--muted-foreground)',
                background: 'none', border: '1px solid var(--border)', borderRadius: '8px',
                padding: '5px 12px', cursor: 'pointer',
              }}
            >
              <Pencil size={12} /> Edit blueprint
            </button>
          )}
          {isAdmin && editMode && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={saveEdit}
                disabled={saving}
                style={{
                  fontSize: 'var(--text-xs)', fontWeight: 600, color: '#fff',
                  backgroundColor: '#00c9c4', border: 'none', borderRadius: '8px',
                  padding: '6px 14px', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? 'Saving…' : 'Save blueprint'}
              </button>
              <button
                onClick={() => { setDraft(null); setSaveError(null); }}
                disabled={saving}
                style={{
                  fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--muted-foreground)',
                  background: 'none', border: '1px solid var(--border)', borderRadius: '8px',
                  padding: '5px 12px', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              {saveError && (
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--infrared-coral)' }}>{saveError}</span>
              )}
            </span>
          )}
          {totalComplete > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              backgroundColor: 'rgba(18,183,106,0.14)', borderRadius: '99px',
              padding: '3px 10px',
            }}>
              <CheckCircle2 size={11} color="#12b76a" />
              <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: '#12b76a' }}>
                {totalComplete} of {totalInterventions} complete
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Wave summary strip */}
      <div style={{ padding: '0 32px', display: 'flex', gap: '12px', marginBottom: '32px' }}>
        {waves.map(wave => {
          const accent = WAVE_ACCENT[wave.number] ?? WAVE_ACCENT[1];
          const waveComplete = wave.interventions.filter(inv => statuses[inv.id] === 'complete').length;
          const pct = wave.interventions.length > 0 ? waveComplete / wave.interventions.length : 0;
          return (
            <div key={wave.number} style={{
              flex: 1,
              backgroundColor: accent.bg,
              border: `1px solid ${accent.border}`,
              borderRadius: '12px',
              overflow: 'hidden',
            }}>
              <div style={{ height: '3px', backgroundColor: accent.color }} />
              <div style={{ padding: '14px 16px' }}>
                <p style={{ fontSize: 'var(--text-xs)', fontWeight: 700, color: accent.color, letterSpacing: '0.5px', marginBottom: '5px' }}>
                  WAVE {String(wave.number).padStart(2, '0')}
                </p>
                <p style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--foreground)', lineHeight: '1.35', marginBottom: '8px' }}>
                  {wave.title}
                </p>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', marginBottom: '10px' }}>
                  {wave.subtitle} · {wave.velocity_label}
                </p>
                {/* Mini progress bar */}
                <div style={{ height: '4px', backgroundColor: 'var(--border)', borderRadius: '99px', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    width: `${Math.round(pct * 100)}%`,
                    backgroundColor: accent.color,
                    borderRadius: '99px',
                    transition: 'width 0.3s ease',
                  }} />
                </div>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', marginTop: '5px' }}>
                  {waveComplete} of {wave.interventions.length} complete
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Waves + interventions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
        {waves.map((wave, waveIdx) => {
          const accent = WAVE_ACCENT[wave.number] ?? WAVE_ACCENT[1];
          return (
            <div key={wave.number} style={{ padding: '0 32px' }}>
              {/* Wave label row */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px',
                paddingBottom: '10px', borderBottom: `2px solid ${accent.color}`,
              }}>
                <div style={{
                  backgroundColor: accent.color,
                  color: '#fff',
                  borderRadius: '6px',
                  padding: '3px 10px',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  flexShrink: 0,
                }}>
                  WAVE {String(wave.number).padStart(2, '0')}
                </div>
                <p style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--foreground)' }}>
                  {wave.title}
                </p>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', marginLeft: 'auto', flexShrink: 0 }}>
                  {wave.velocity_label}
                </span>
              </div>

              {/* Interventions */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {wave.interventions.map((inv, invIdx) => {
                  const isOpen   = expanded.has(inv.id);
                  const status   = statuses[inv.id] ?? 'not_started';
                  const statusSt = STATUS_STYLE[status];

                  return (
                    <div key={inv.id} style={{
                      backgroundColor: 'var(--background)',
                      border: '1px solid var(--border)',
                      borderLeft: `4px solid ${accent.color}`,
                      borderRadius: '10px',
                      overflow: 'hidden',
                      transition: 'box-shadow 0.15s ease',
                    }}>
                      {/* Row — click to expand */}
                      <div
                        onClick={() => toggleExpanded(inv.id)}
                        style={{
                          padding: '14px 16px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          cursor: 'pointer',
                        }}
                      >
                        {/* ID badge */}
                        <span style={{
                          fontSize: 'var(--text-xs)',
                          fontWeight: 700,
                          color: accent.color,
                          backgroundColor: accent.bg,
                          border: `1px solid ${accent.border}`,
                          borderRadius: '5px',
                          padding: '2px 8px',
                          flexShrink: 0,
                          fontFamily: 'monospace',
                        }}>
                          {inv.id}
                        </span>

                        {/* Title (editable in curation mode) */}
                        {editMode ? (
                          <input
                            value={inv.title}
                            onClick={e => e.stopPropagation()}
                            onChange={e => updateDraftInv(waveIdx, invIdx, 'title', e.target.value)}
                            style={{
                              flex: 1,
                              fontSize: 'var(--text-sm)',
                              fontWeight: 600,
                              color: 'var(--foreground)',
                              lineHeight: '1.35',
                              border: '1px solid var(--border)',
                              borderRadius: '6px',
                              padding: '6px 10px',
                              backgroundColor: 'var(--card)',
                            }}
                          />
                        ) : (
                          <p style={{
                            flex: 1,
                            fontSize: 'var(--text-sm)',
                            fontWeight: 600,
                            color: 'var(--foreground)',
                            lineHeight: '1.35',
                          }}>
                            {inv.title}
                          </p>
                        )}

                        {/* Curation mode: effort select. View mode: status toggle. */}
                        {editMode ? (
                          <select
                            value={inv.effort}
                            onClick={e => e.stopPropagation()}
                            onChange={e => updateDraftInv(waveIdx, invIdx, 'effort', e.target.value)}
                            style={{
                              fontSize: 'var(--text-xs)',
                              fontWeight: 600,
                              color: 'var(--foreground)',
                              backgroundColor: 'var(--card)',
                              border: '1px solid var(--border)',
                              borderRadius: '6px',
                              padding: '4px 8px',
                              flexShrink: 0,
                              cursor: 'pointer',
                            }}
                          >
                            <option value="LOW">LOW</option>
                            <option value="MEDIUM">MEDIUM</option>
                            <option value="HIGH">HIGH</option>
                          </select>
                        ) : (
                          <button
                            onClick={e => { e.stopPropagation(); cycleStatus(inv.id); }}
                            style={{
                              fontSize: 'var(--text-xs)',
                              fontWeight: 600,
                              color: statusSt.color,
                              backgroundColor: statusSt.bg,
                              border: `1px solid ${statusSt.border}`,
                              borderRadius: '99px',
                              padding: '4px 12px',
                              cursor: 'pointer',
                              flexShrink: 0,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {STATUS_LABEL[status]}
                          </button>
                        )}

                        {/* Chevron */}
                        <ChevronDown
                          size={14}
                          color="var(--muted-foreground)"
                          style={{
                            flexShrink: 0,
                            transform: isOpen ? 'rotate(180deg)' : 'none',
                            transition: 'transform 0.2s ease',
                          }}
                        />
                      </div>

                      {/* Expanded description (editable in curation mode) */}
                      {isOpen && (
                        <div style={{
                          padding: '0 16px 16px 16px',
                          borderTop: '1px solid var(--border)',
                          paddingTop: '14px',
                        }}>
                          {editMode ? (
                            <textarea
                              value={inv.description}
                              rows={Math.max(3, Math.ceil(inv.description.length / 90))}
                              onClick={e => e.stopPropagation()}
                              onChange={e => updateDraftInv(waveIdx, invIdx, 'description', e.target.value)}
                              style={{
                                width: '100%',
                                fontSize: 'var(--text-sm)',
                                color: 'var(--foreground)',
                                lineHeight: '1.7',
                                border: '1px solid var(--border)',
                                borderRadius: '6px',
                                padding: '10px 12px',
                                backgroundColor: 'var(--card)',
                                resize: 'vertical',
                                fontFamily: 'inherit',
                              }}
                            />
                          ) : (
                            <p style={{
                              fontSize: 'var(--text-sm)',
                              color: 'var(--muted-foreground)',
                              lineHeight: '1.7',
                            }}>
                              {inv.description}
                            </p>
                          )}
                          {!editMode && customerId != null && (
                            <ItemContent customerId={customerId} inv={inv} drafts={drafts} isAdmin={isAdmin}
                              actionId={(inv.target_variables || []).map(b => actionByBelief[b]).find(id => id != null) ?? null} />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
